import { Pipe, PipeTransform } from '@angular/core';
import { Dictionary } from '@ngrx/entity';
import * as urlTemplate from 'url-template';

import { FilterTypes } from '@shared/flight-itinerary/entities/filters';
import { MULTIPLE_CARRIERS, MULTIPLES } from '@flight-results/constants/filters.constants';
import { flightResultsApi } from '@flight-results/flight-results.api';
import { PriceRangeCurrencyFormatterPipe } from '@shared/filters/pipes/price-range-currency-formatter.pipe';
import { AirlineImagePipe } from '@app/pipes/airline-image.pipe';
import { BusCompany } from '@app/entities/bus-company';
import { BusCompanyImagePipe } from '@app/pipes/bus-company-image.pipe';
import {
  AirFilterResponseVm, AirFilterVm, AirValueFilterVm, AirFilterInputType
} from '@shared/magic-filter-bar/entities/filter-types';

@Pipe({
  name: 'airFiltersVm',
  standalone: false
})
export class AirFiltersVmPipe implements PipeTransform {

  private readonly allowFilters: FilterTypes[] = [
    FilterTypes.Airlines,
    FilterTypes.Alliances,
    FilterTypes.BusClass,
    FilterTypes.ArrivalAirports,
    FilterTypes.DepartureAirports,
    FilterTypes.Stops,
    FilterTypes.PriceRange,
    FilterTypes.SectorDurations,
    FilterTypes.DepartureTimes,
    FilterTypes.Tags,
    FilterTypes.AirFilterTabs
  ];

  constructor(
    private readonly airlineImagePipe: AirlineImagePipe,
    private readonly busCompanyImagePipe: BusCompanyImagePipe,
    private readonly priceRangeCurrencyFormatterPipe: PriceRangeCurrencyFormatterPipe
  ) { }

  transform(
    airFilters: AirFilterResponseVm,
    busCompanies: Dictionary<BusCompany> | undefined = undefined,
    isBuses: boolean | undefined = false
  ): AirFilterVm[] {
    const filtersVm = Object.keys(airFilters)
      .filter(key => this.allowFilters.includes(key as FilterTypes))
      .map(key => {
        let value = airFilters[key];
        if (key === FilterTypes.Airlines) {
          value = this.getAirlinesValue(airFilters, isBuses, busCompanies);
        }
        if (key === FilterTypes.Alliances) {
          value = this.getAlliancesValue(airFilters);
        }
        if (key === FilterTypes.AirPriceRanges) {
          value = this.getAirPriceRangesValue(airFilters);
        }
        const order = isBuses ? this.getOrderByTypeForBuses(key) : this.getOrderByTypeForFlights(key);
        return {
          type: key as FilterTypes,
          value: value,
          filter: AirFilterInputType[key],
          order: order
        };
      }).sort((filterA, filterB) => filterB.order - filterA.order);

    return this.removeUnusedFilters(filtersVm, isBuses);
  }

  private getAirlinesValue(
    airFilters: AirFilterResponseVm, isBuses: boolean, busCompanies: Dictionary<BusCompany>
  ): AirValueFilterVm {
    return airFilters[FilterTypes.Airlines].map(airline => {
      const isMultiple = airline.Text === MULTIPLE_CARRIERS;
      const airlineCode = !isMultiple ? airline.Text : MULTIPLES;
      return {
        ...airline,
        Image: isBuses ? this.busCompanyImagePipe.transform(airlineCode) : this.airlineImagePipe.transform(airlineCode),
        Label: isBuses ? busCompanies?.[airline?.Text]?.name ?? '' : airline.Label ?? ''
      };
    });
  }

  private getAlliancesValue(airFilters: AirFilterResponseVm): AirValueFilterVm {
    return airFilters[FilterTypes.Alliances].map(alliance => {
      return {
        ...alliance,
        Image: urlTemplate.parse(flightResultsApi.alliance_images).expand({ alliance: alliance.Text })
      };
    });
  }

  private getAirPriceRangesValue(airFilters: AirFilterResponseVm): AirValueFilterVm {
    return airFilters[FilterTypes.AirPriceRanges].map(range => {
      return {
        ...range,
        Label: this.priceRangeCurrencyFormatterPipe.transform(range.Label)
      };
    });
  }

  private removeUnusedFilters(filtersVm: AirFilterVm[], isBuses: boolean): AirFilterVm[] {
    if (isBuses) {
      return filtersVm.filter(filter =>
        filter.type !== FilterTypes.Stops && filter.type !== FilterTypes.Alliances && filter.type !== FilterTypes.Tags
      );
    } else {
      return filtersVm.filter(filter => filter.type !== FilterTypes.BusClass);
    }
  }

  private getOrderByTypeForFlights(key: string): number {
    switch (key) {
      case FilterTypes.AirPriceRanges:
      case FilterTypes.BusClass:
        return 1;
      case FilterTypes.ArrivalAirports:
        return 2;
      case FilterTypes.DepartureAirports:
        return 3;
      case FilterTypes.Airlines:
        return 4;
      case FilterTypes.PriceRange:
        return 5;
      case FilterTypes.Stops:
        return 6;
      case FilterTypes.SectorDurations:
        return -1;
      case FilterTypes.Alliances:
        return -2;
      default:
        return 0;
    }
  }

  private getOrderByTypeForBuses(key: string): number {
    switch (key) {
      case FilterTypes.AirPriceRanges:
        return 1;
      case FilterTypes.ArrivalAirports:
        return 2;
      case FilterTypes.DepartureAirports:
        return 3;
      case FilterTypes.Airlines:
        return 4;
      case FilterTypes.BusClass:
        return 5;
      case FilterTypes.PriceRange:
        return 6;
      case FilterTypes.Stops:
        return 7;
      case FilterTypes.SectorDurations:
        return -1;
      case FilterTypes.Alliances:
        return -2;
      default:
        return 0;
    }
  }
}
